/**
 * Budget module — redesigned (iter 11) with sub-tabs.
 *
 * Sub-tabs:
 *  01 Resumen              — KPI cards + 12-month projection chart
 *  02 Inversión inicial    — startup_costs (one-time investments)
 *  03 Costes mensuales     — fixed_monthly + variable_monthly
 *  04 Ingresos             — revenue_streams + unit economics
 *  05 P&L · Balance · BE   — Year-1 P&L, balance sheet, break-even
 *  06 5 años · Escenarios  — 5-year projection + best/base/worst scenarios
 */
import { useEffect, useMemo, useState } from "react";
import {
  Plus,
  Trash2,
  Gauge,
  PiggyBank,
  Receipt,
  Coins,
  FileBarChart,
  TrendingUp,
  Repeat,
  Zap,
} from "lucide-react";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  Tooltip,
  CartesianGrid,
  ResponsiveContainer,
  Legend,
  BarChart,
  Bar,
} from "recharts";
import { useLang } from "@/contexts/LanguageContext";
import {
  SectionCard,
  FieldLabel,
  TextInput,
  TextArea,
  PrimaryButton,
  SecondaryButton,
  AIBadge,
  Overline,
  SubTabs,
} from "@/components/ui-kit";
import AIAssistant from "@/components/AIAssistant";
import {
  computeBreakEven,
  computePL,
  computeBalance,
  computeFiveYears,
  computeScenarios,
  computeMonthlyProjection,
  year1Totals,
  HORIZON_MONTHS,
} from "@/lib/finance";

const empty = {
  startup_costs: [],
  fixed_monthly: [],
  variable_monthly: [],
  revenue_streams: [],
  tax_rate: 25,
  initial_capital: 0,
  unit_avg_revenue: 0,
  unit_avg_variable_cost: 0,
  yearly_revenue_growth: 20,
  yearly_expense_growth: 5,
  scenarios: {
    best_revenue_mult: 1.30,
    worst_revenue_mult: 0.70,
    best_expense_mult: 0.90,
    worst_expense_mult: 1.15,
  },
};

const MONTHS_ES = ["Ene", "Feb", "Mar", "Abr", "May", "Jun", "Jul", "Ago", "Sep", "Oct", "Nov", "Dic"];
const MONTHS_EN = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

function CategoryList({
  items, onAdd, onUpdate, onRemove, addLabel, testIdPrefix, placeholder,
  yearTotal, lang, accentRecurring = "#2563EB", accentOneTime = "#B89154",
  nameKey = "name",
}) {
  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <Overline>{placeholder}</Overline>
        <SecondaryButton onClick={onAdd} testId={`${testIdPrefix}-add-btn`}>
          <Plus className="h-4 w-4" strokeWidth={1.5} />
          {addLabel}
        </SecondaryButton>
      </div>
      {items.length === 0 ? (
        <div className="text-sm text-[#8A8886] py-6 text-center border border-dashed border-[#E8E6E1] rounded-sm">
          {lang === "es" ? "Sin partidas. Añade la primera." : "No entries. Add the first one."}
        </div>
      ) : (
        <div className="space-y-3">
          {items.map((rawRow, i) => (
            <StreamRow
              key={i}
              idx={i}
              raw={rawRow}
              nameKey={nameKey}
              testIdPrefix={testIdPrefix}
              onUpdate={onUpdate}
              onRemove={onRemove}
              lang={lang}
              accentRecurring={accentRecurring}
              accentOneTime={accentOneTime}
            />
          ))}
          <div className="border-t border-[#E8E6E1] pt-3 mt-3 flex items-center justify-between">
            <Overline>{lang === "es" ? "Σ Año 1" : "Σ Year 1"}</Overline>
            <div className="font-mono text-lg text-[#1A1A1A]" style={{ fontFamily: '"IBM Plex Mono", monospace' }}>
              €{(yearTotal || 0).toLocaleString(undefined, { maximumFractionDigits: 0 })}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

/**
 * Reusable row for revenue streams and expense lines. Same schema:
 *   {name|item, type, amount, start_month, recurrence, monthly_growth_rate, duration_months}
 * `nameKey` controls whether the concept is stored as `name` (revenue) or
 * `item` (legacy expense field name — kept for backward compat with old data).
 */
function StreamRow({ idx, raw, nameKey, testIdPrefix, onUpdate, onRemove, lang, accentRecurring, accentOneTime }) {
  const r = {
    name: raw[nameKey] ?? raw.name ?? raw.item ?? "",
    type: raw.type === "one-time" ? "one-time" : "recurring",
    amount: raw.amount != null ? raw.amount : (raw.monthly_amount ?? 0),
    start_month: raw.start_month || 1,
    recurrence: raw.recurrence || "monthly",
    monthly_growth_rate: raw.monthly_growth_rate != null
      ? raw.monthly_growth_rate
      : (raw.growth_rate ?? 0),
  };
  return (
    <div
      className="border border-[#E8E6E1] bg-white rounded-sm p-4 space-y-3"
      data-testid={`${testIdPrefix}-row-${idx}`}
    >
      {/* Row 1 — concept + type toggle + delete */}
      <div className="grid grid-cols-1 md:grid-cols-[1fr_auto_auto] gap-3 items-center">
        <TextInput
          value={r.name}
          onChange={(x) => onUpdate(idx, nameKey, x)}
          placeholder={lang === "es" ? "Concepto" : "Concept"}
          testId={`${testIdPrefix}-${idx}-${nameKey}`}
        />
        <div className="inline-flex border border-[#E8E6E1] rounded-sm overflow-hidden">
          <button
            type="button"
            onClick={() => onUpdate(idx, "type", "one-time")}
            data-testid={`${testIdPrefix}-${idx}-type-onetime`}
            className={`px-3 py-2 text-xs uppercase tracking-[0.16em] inline-flex items-center gap-1.5 transition-colors ${
              r.type === "one-time" ? "text-[#FBFAF7]" : "bg-white text-[#5C5B5A] hover:text-[#1A1A1A]"
            }`}
            style={r.type === "one-time" ? { backgroundColor: accentOneTime } : {}}
          >
            <Zap className="h-3.5 w-3.5" strokeWidth={1.5} />
            {lang === "es" ? "Puntual" : "One-time"}
          </button>
          <button
            type="button"
            onClick={() => onUpdate(idx, "type", "recurring")}
            data-testid={`${testIdPrefix}-${idx}-type-recurring`}
            className={`px-3 py-2 text-xs uppercase tracking-[0.16em] inline-flex items-center gap-1.5 border-l border-[#E8E6E1] transition-colors ${
              r.type === "recurring" ? "text-[#F9F8F6]" : "bg-white text-[#5C5B5A] hover:text-[#1A1A1A]"
            }`}
            style={r.type === "recurring" ? { backgroundColor: accentRecurring } : {}}
          >
            <Repeat className="h-3.5 w-3.5" strokeWidth={1.5} />
            {lang === "es" ? "Recurrente" : "Recurring"}
          </button>
        </div>
        <button
          onClick={() => onRemove(idx)}
          className="text-[#8A8886] hover:text-[#9E2A2B] justify-self-end"
          data-testid={`${testIdPrefix}-${idx}-remove`}
          aria-label="remove"
        >
          <Trash2 className="h-4 w-4" strokeWidth={1.5} />
        </button>
      </div>

      {/* Row 2 — amount + start + (if recurring) recurrence + growth */}
      <div className={`grid gap-3 ${r.type === "recurring" ? "grid-cols-2 md:grid-cols-4" : "grid-cols-2"}`}>
        <div>
          <FieldLabel>
            {r.type === "one-time"
              ? (lang === "es" ? "Importe (€)" : "Amount (€)")
              : (lang === "es" ? "Importe inicial (€)" : "Initial amount (€)")}
          </FieldLabel>
          <TextInput
            type="number"
            value={r.amount}
            onChange={(x) => onUpdate(idx, "amount", parseFloat(x) || 0)}
            testId={`${testIdPrefix}-${idx}-amount`}
          />
        </div>
        <div>
          <FieldLabel>{lang === "es" ? "Empieza en" : "Starts at"}</FieldLabel>
          <select
            value={r.start_month}
            onChange={(e) => onUpdate(idx, "start_month", parseInt(e.target.value, 10) || 1)}
            className="w-full border border-[#E8E6E1] bg-white rounded-sm focus:ring-1 focus:ring-[#2563EB] focus:border-[#2563EB] text-base px-3 py-2.5 outline-none transition-all"
            data-testid={`${testIdPrefix}-${idx}-start`}
          >
            {Array.from({ length: HORIZON_MONTHS }, (_, k) => k + 1).map((m) => {
              const yr = Math.floor((m - 1) / 12) + 1;
              const mInYr = ((m - 1) % 12) + 1;
              return (
                <option key={m} value={m}>
                  {`M${m} · ${lang === "es" ? "Año" : "Year"} ${yr} mes ${mInYr}`}
                </option>
              );
            })}
          </select>
        </div>
        {r.type === "recurring" && (
          <>
            <div>
              <FieldLabel>{lang === "es" ? "Periodicidad" : "Recurrence"}</FieldLabel>
              <select
                value={r.recurrence}
                onChange={(e) => onUpdate(idx, "recurrence", e.target.value)}
                className="w-full border border-[#E8E6E1] bg-white rounded-sm focus:ring-1 focus:ring-[#2563EB] focus:border-[#2563EB] text-base px-3 py-2.5 outline-none transition-all"
                data-testid={`${testIdPrefix}-${idx}-recurrence`}
              >
                <option value="monthly">{lang === "es" ? "Mensual" : "Monthly"}</option>
                <option value="quarterly">{lang === "es" ? "Trimestral" : "Quarterly"}</option>
                <option value="annual">{lang === "es" ? "Anual" : "Annual"}</option>
              </select>
            </div>
            <div>
              <FieldLabel hint={lang === "es" ? "% mes a mes (compuesto)" : "% month over month (compound)"}>
                {lang === "es" ? "Crecimiento / mes (%)" : "Growth / month (%)"}
              </FieldLabel>
              <TextInput
                type="number"
                value={r.monthly_growth_rate}
                onChange={(x) => onUpdate(idx, "monthly_growth_rate", parseFloat(x) || 0)}
                testId={`${testIdPrefix}-${idx}-growth`}
              />
            </div>
          </>
        )}
      </div>
    </div>
  );
}

export default function BudgetModule({ data, project, onSave }) {
  const { t, lang } = useLang();
  const [v, setV] = useState({ ...empty, ...(data || {}) });
  const [tab, setTab] = useState("overview");
  const [horizon, setHorizon] = useState("y1"); // "y1" | "y5" — Overview chart range
  // Stress Test sliders (transient — never persisted to DB)
  const [stress, setStress] = useState({
    revenueCut: 0,       // % reduction of all revenue (0-50)
    revenueDelay: 0,     // shift revenue start_month forward by N months (0-12)
    expenseHike: 0,      // % increase on all expenses (0-50)
    startupOverrun: 0,   // % increase on startup_costs (0-100)
  });
  const stressActive = stress.revenueCut || stress.revenueDelay || stress.expenseHike || stress.startupOverrun;

  useEffect(() => {
    setV((prev) => {
      const next = {
        ...empty,
        ...(data || {}),
        scenarios: { ...empty.scenarios, ...((data && data.scenarios) || {}) },
      };
      try { if (JSON.stringify(prev) === JSON.stringify(next)) return prev; } catch (e) { void e; }
      return next;
    });
  }, [data]);
  useEffect(() => {
    onSave?.(v);
  }, [v, onSave]);

  const set = (k, val) => setV((p) => ({ ...p, [k]: val }));
  const setScenario = (k, val) =>
    setV((p) => ({ ...p, scenarios: { ...p.scenarios, [k]: val } }));

  function listOps(key, blank) {
    return {
      add: () => setV((p) => ({ ...p, [key]: [...(p[key] || []), { ...blank }] })),
      update: (i, k, val) =>
        setV((p) => ({ ...p, [key]: p[key].map((x, idx) => (idx === i ? { ...x, [k]: val } : x)) })),
      remove: (i) => setV((p) => ({ ...p, [key]: p[key].filter((_, idx) => idx !== i) })),
    };
  }
  const startup = listOps("startup_costs", { item: "", amount: 0 });
  const fixed = listOps("fixed_monthly", {
    item: "", type: "recurring", amount: 0, start_month: 1, recurrence: "monthly", monthly_growth_rate: 0, duration_months: 0,
  });
  const variable = listOps("variable_monthly", {
    item: "", type: "recurring", amount: 0, start_month: 1, recurrence: "monthly", monthly_growth_rate: 0, duration_months: 0,
  });

  const addRevenue = () =>
    setV((p) => ({
      ...p,
      revenue_streams: [
        ...(p.revenue_streams || []),
        {
          name: "",
          type: "recurring",
          amount: 0,
          start_month: 1,
          recurrence: "monthly",
          monthly_growth_rate: 0,
          duration_months: 0,
        },
      ],
    }));
  const updateRevenue = (i, k, val) =>
    setV((p) => ({
      ...p,
      revenue_streams: p.revenue_streams.map((x, idx) => (idx === i ? { ...x, [k]: val } : x)),
    }));
  const removeRevenue = (i) =>
    setV((p) => ({ ...p, revenue_streams: p.revenue_streams.filter((_, idx) => idx !== i) }));

  const totalStartup = (v.startup_costs || []).reduce((s, x) => s + (parseFloat(x.amount) || 0), 0);

  // M1 burn = sum of every fixed + variable expense line active in month 1.
  // (Replaces the old "totalFixed + totalVariable" which assumed every line
  //  starts in M1 and is monthly — no longer true.)
  const monthM = (line, m) => {
    const e = {
      type: line.type === "one-time" ? "one-time" : "recurring",
      amount: parseFloat(line.amount) || 0,
      start_month: Math.max(1, parseInt(line.start_month, 10) || 1),
      recurrence: ["monthly", "quarterly", "annual"].includes(line.recurrence) ? line.recurrence : "monthly",
      monthly_growth_rate: parseFloat(line.monthly_growth_rate) || 0,
      duration_months: parseInt(line.duration_months, 10) || 0,
    };
    if (m < e.start_month) return 0;
    if (e.duration_months > 0 && m >= e.start_month + e.duration_months) return 0;
    const offset = m - e.start_month;
    if (e.type === "one-time") return offset === 0 ? e.amount : 0;
    if (e.recurrence === "quarterly" && offset % 3 !== 0) return 0;
    if (e.recurrence === "annual" && offset % 12 !== 0) return 0;
    return e.amount * Math.pow(1 + e.monthly_growth_rate / 100, offset);
  };
  const m1Fixed = (v.fixed_monthly || []).reduce((s, x) => s + monthM(x, 1), 0);
  const m1Variable = (v.variable_monthly || []).reduce((s, x) => s + monthM(x, 1), 0);
  const totalMonthlyExpenses = m1Fixed + m1Variable; // M1 burn (Overview KPI)
  // KPI cards still show "Burn" / "Costes" sums for M1, not yearly.
  const totalFixed = m1Fixed;
  const totalVariable = m1Variable;
  // "Ingreso recurrente equivalente M1" — sum of all streams that pay in month 1
  const month1Revenue = useMemo(() => {
    return (v.revenue_streams || []).reduce((s, r) => {
      const amount = parseFloat(r.amount != null ? r.amount : r.monthly_amount) || 0;
      const start = Math.max(1, parseInt(r.start_month, 10) || 1);
      if (start !== 1) return s;
      return s + amount;
    }, 0);
  }, [v.revenue_streams]);

  // 60-month projection across the entire 5-year horizon.
  const fullProjection = useMemo(() => {
    const months = lang === "es" ? MONTHS_ES : MONTHS_EN;
    return computeMonthlyProjection({
      revenue_streams: v.revenue_streams || [],
      fixed_monthly: v.fixed_monthly || [],
      variable_monthly: v.variable_monthly || [],
      totalStartup,
      horizonMonths: HORIZON_MONTHS,
      monthLabels: months,
    });
  }, [v.revenue_streams, v.fixed_monthly, v.variable_monthly, totalStartup, lang]);

  // Year-1 slice (for the Overview "12 months" chart and Year-1 P&L).
  const projection = useMemo(() => fullProjection.slice(0, 12).map((row, idx) => ({
    ...row,
    month: row.monthLabel,
    monthIdx: idx,
  })), [fullProjection]);

  // 5-year monthly view: same 60 rows, with a "Y1 · Ene" style label.
  const projection5y = useMemo(() => fullProjection.map((row) => ({
    ...row,
    month: row.monthFullLabel,
  })), [fullProjection]);

  // === Stress Test projection ============================================
  // Apply the slider deltas to a CLONE of the streams + expenses and
  // re-run the projection. Never mutates the saved data.
  const stressedProjection = useMemo(() => {
    if (!stressActive) return null;
    const cutMult = 1 - (parseFloat(stress.revenueCut) || 0) / 100;
    const expMult = 1 + (parseFloat(stress.expenseHike) || 0) / 100;
    const startupMult = 1 + (parseFloat(stress.startupOverrun) || 0) / 100;
    const delay = parseInt(stress.revenueDelay, 10) || 0;

    const stressedRevenue = (v.revenue_streams || []).map((r) => ({
      ...r,
      amount: (parseFloat(r.amount != null ? r.amount : r.monthly_amount) || 0) * cutMult,
      start_month: Math.min(HORIZON_MONTHS, (parseInt(r.start_month, 10) || 1) + delay),
    }));
    const stressedFixed = (v.fixed_monthly || []).map((e) => ({
      ...e,
      amount: (parseFloat(e.amount) || 0) * expMult,
    }));
    const stressedVariable = (v.variable_monthly || []).map((e) => ({
      ...e,
      amount: (parseFloat(e.amount) || 0) * expMult,
    }));
    const months = lang === "es" ? MONTHS_ES : MONTHS_EN;
    return computeMonthlyProjection({
      revenue_streams: stressedRevenue,
      fixed_monthly: stressedFixed,
      variable_monthly: stressedVariable,
      totalStartup: totalStartup * startupMult,
      horizonMonths: HORIZON_MONTHS,
      monthLabels: months,
    });
  }, [stress, stressActive, v.revenue_streams, v.fixed_monthly, v.variable_monthly, totalStartup, lang]);

  // Comparison data for the stress chart (baseline vs stressed cum. cash)
  const stressCompareData = useMemo(() => {
    if (!stressedProjection) return [];
    return fullProjection.map((row, idx) => ({
      month: row.monthFullLabel,
      baseline: row.cumulative,
      stressed: stressedProjection[idx]?.cumulative ?? 0,
      baselineCashFlow: row.cashFlow,
      stressedCashFlow: stressedProjection[idx]?.cashFlow ?? 0,
    }));
  }, [fullProjection, stressedProjection]);

  // Stress KPIs vs baseline
  const stressKpis = useMemo(() => {
    if (!stressedProjection) return null;
    const baselineBE = fullProjection.find((r) => r.cumulative >= 0)?.monthNumber || null;
    const stressedBE = stressedProjection.find((r) => r.cumulative >= 0)?.monthNumber || null;
    const baselineY1 = fullProjection.slice(0, 12).reduce((s, x) => s + x.cashFlow, 0);
    const stressedY1 = stressedProjection.slice(0, 12).reduce((s, x) => s + x.cashFlow, 0);
    const baselineLowest = Math.min(...fullProjection.map((r) => r.cumulative));
    const stressedLowest = Math.min(...stressedProjection.map((r) => r.cumulative));
    const initialCapital = parseFloat(v.initial_capital) || 0;
    // Capital required = the gap (negative cash) the user would have to cover
    // to keep cash ≥ 0 across the full 60-month horizon. Zero if you survive.
    const capitalGap = Math.max(0, -stressedLowest);
    const survives = capitalGap <= initialCapital;
    return {
      baselineBE, stressedBE,
      baselineY1, stressedY1,
      baselineLowest, stressedLowest,
      capitalGap,
      extraCapitalNeeded: Math.max(0, capitalGap - initialCapital),
      survives,
    };
  }, [stressedProjection, fullProjection, v.initial_capital]);

  const fin = useMemo(() => {
    const taxRate = parseFloat(v.tax_rate) || 0;
    const initialCapital = parseFloat(v.initial_capital) || 0;
    const totals = year1Totals(fullProjection, totalFixed, totalVariable);
    const pl = computePL({
      revenue: totals.revenue,
      fixedExpenses: totals.fixedYearly,
      variableExpenses: totals.variableYearly,
      taxRate,
    });
    const balance = computeBalance({
      initialCapital,
      startupCosts: totalStartup,
      year1Revenue: totals.revenue,
      year1Expenses: totals.expenses,
      year1Tax: pl.tax,
    });
    const be = computeBreakEven({
      unitRevenue: parseFloat(v.unit_avg_revenue) || 0,
      unitVariable: parseFloat(v.unit_avg_variable_cost) || 0,
      totalFixedMonthly: totalFixed,
      monthlyProjection: fullProjection,
    });
    const fiveYear = computeFiveYears({
      monthlyProjection: fullProjection,
      taxRate,
    });
    const scenarios = computeScenarios({
      y1Revenue: totals.revenue,
      fixedYearly: totals.fixedYearly,
      variableYearly: totals.variableYearly,
      taxRate,
      scenarios: v.scenarios || empty.scenarios,
    });
    return { totals, pl, balance, be, fiveYear, scenarios };
  }, [
    fullProjection,
    totalFixed,
    totalVariable,
    totalStartup,
    v.tax_rate,
    v.initial_capital,
    v.unit_avg_revenue,
    v.unit_avg_variable_cost,
    v.scenarios,
  ]);

  // Memoised 5-year cumulative sums (used in summary cards). Cheap (5 items)
  // but keeps render pure and avoids re-running `.reduce()` every paint.
  const fiveYearCumulativeExpenses = useMemo(
    () => fin.fiveYear.reduce((s, y) => s + y.expenses, 0),
    [fin.fiveYear]
  );
  const fiveYearCumulativeRevenue = useMemo(
    () => fin.fiveYear.reduce((s, y) => s + y.revenue, 0),
    [fin.fiveYear]
  );

  const suggestions = lang === "es"
    ? [
        "Estima los gastos de arranque típicos para este negocio (en EUR)",
        "Lista los gastos fijos mensuales más comunes",
        "Propón fuentes de ingresos con cantidad mensual y % de crecimiento",
        "Sugiere un ticket medio por cliente y coste variable razonables",
        "Recomienda % de crecimiento anual realista a 5 años (ingresos y gastos)",
      ]
    : [
        "Estimate typical startup costs (EUR)",
        "List the most common fixed monthly expenses",
        "Propose revenue streams with monthly amount and growth rate",
        "Suggest realistic average price per customer and variable cost",
        "Recommend realistic yearly growth (revenue and expenses) for 5 years",
      ];

  const tabs = [
    { key: "overview",   label: lang === "es" ? "Resumen"          : "Overview",       icon: Gauge },
    { key: "investment", label: lang === "es" ? "Inversión inicial" : "Initial investment", icon: PiggyBank },
    { key: "costs",      label: lang === "es" ? "Costes mensuales" : "Monthly costs",  icon: Receipt },
    { key: "revenue",    label: lang === "es" ? "Ingresos"         : "Revenue",        icon: Coins },
    { key: "statements", label: lang === "es" ? "P&L · Balance · Break-even" : "P&L · Balance · Break-even", icon: FileBarChart },
    { key: "scenarios",  label: lang === "es" ? "5 años · Escenarios" : "5 years · Scenarios", icon: TrendingUp },
  ];

  return (
    <>
      <SectionCard
        title={t("module.budget")}
        subtitle={lang === "es" ? "Inversión, costes, ingresos, P&L, escenarios y proyección a 5 años." : "Investment, costs, revenue, P&L, scenarios and 5-year projection."}
        action={
          <div className="flex items-center gap-3">
            <AIBadge />
            <AIAssistant module="budget" context={{ project: project?.name }} suggestions={suggestions} />
          </div>
        }
        testId="budget-section"
      >
        <SubTabs tabs={tabs} active={tab} onChange={setTab} testIdPrefix="budget-subtab" />

        {/* OVERVIEW */}
        {tab === "overview" && (
          <div data-testid="budget-tab-overview" className="space-y-8">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 font-mono" style={{ fontFamily: '"IBM Plex Mono", monospace' }}>
              <div className="border border-[#E8E6E1] rounded-sm p-5">
                <Overline>{t("budget.startup")}</Overline>
                <div className="text-2xl mt-2 text-[#1A1A1A]" data-testid="ovw-startup-total">€{totalStartup.toLocaleString()}</div>
                <p className="text-[10px] text-[#8A8886] mt-1">{lang === "es" ? "Una vez" : "One-time"}</p>
              </div>
              <div className="border border-[#E8E6E1] rounded-sm p-5">
                <Overline>{t("budget.fixed")}</Overline>
                <div className="text-2xl mt-2 text-[#1A1A1A]" data-testid="ovw-fixed-total">€{totalFixed.toLocaleString()}</div>
                <p className="text-[10px] text-[#8A8886] mt-1">{lang === "es" ? "M1" : "M1"}</p>
              </div>
              <div className="border border-[#E8E6E1] rounded-sm p-5">
                <Overline>{t("budget.variable")}</Overline>
                <div className="text-2xl mt-2 text-[#1A1A1A]" data-testid="ovw-variable-total">€{totalVariable.toLocaleString()}</div>
                <p className="text-[10px] text-[#8A8886] mt-1">{lang === "es" ? "M1" : "M1"}</p>
              </div>
              <div className="border border-[#2563EB] rounded-sm p-5 bg-[#2563EB] text-[#F9F8F6]">
                <div className="text-xs uppercase tracking-[0.2em] text-[#F5EFE6]/80">
                  {lang === "es" ? "Burn mes 1" : "Month 1 burn"}
                </div>
                <div className="text-2xl mt-2" data-testid="ovw-burn-total">€{totalMonthlyExpenses.toLocaleString()}</div>
                <p className="text-[10px] text-[#F5EFE6]/70 mt-1">
                  {lang === "es" ? "Ingreso M1: " : "M1 revenue: "}€{month1Revenue.toLocaleString()}
                </p>
              </div>
            </div>

            {/* Horizon switch: Year 1 (monthly) vs 5 years (monthly) */}
            <div className="flex items-center justify-between gap-3">
              <Overline>
                {horizon === "y1"
                  ? (lang === "es" ? "Ingresos vs Gastos · Año 1" : "Revenue vs Expenses · Year 1")
                  : (lang === "es" ? "Ingresos vs Gastos · 5 años (60 meses)" : "Revenue vs Expenses · 5 years (60 months)")}
              </Overline>
              <div className="inline-flex border border-[#E8E6E1] rounded-sm overflow-hidden" data-testid="horizon-toggle">
                <button
                  type="button"
                  onClick={() => setHorizon("y1")}
                  data-testid="horizon-y1-btn"
                  className={`px-3 py-1.5 text-xs uppercase tracking-[0.18em] transition-colors ${
                    horizon === "y1"
                      ? "bg-[#2563EB] text-[#F9F8F6]"
                      : "bg-white text-[#5C5B5A] hover:text-[#1A1A1A]"
                  }`}
                >
                  {lang === "es" ? "Año 1" : "Year 1"}
                </button>
                <button
                  type="button"
                  onClick={() => setHorizon("y5")}
                  data-testid="horizon-y5-btn"
                  className={`px-3 py-1.5 text-xs uppercase tracking-[0.18em] transition-colors border-l border-[#E8E6E1] ${
                    horizon === "y5"
                      ? "bg-[#2563EB] text-[#F9F8F6]"
                      : "bg-white text-[#5C5B5A] hover:text-[#1A1A1A]"
                  }`}
                >
                  {lang === "es" ? "5 años" : "5 years"}
                </button>
              </div>
            </div>

            <div>
              <div className="h-72 mt-3" style={{ minWidth: 0, minHeight: 240 }}>
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={horizon === "y1" ? projection : projection5y} margin={{ top: 16, right: 12, left: 0, bottom: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#E8E6E1" vertical={false} />
                    <XAxis dataKey="month" stroke="#8A8886" fontSize={11} interval={horizon === "y5" ? 5 : 0} />
                    <YAxis stroke="#8A8886" fontSize={12} />
                    <Tooltip contentStyle={{ background: "#fff", border: "1px solid #E8E6E1", borderRadius: 2, fontFamily: "IBM Plex Mono, monospace", fontSize: 12 }} formatter={(val) => `€${Number(val).toLocaleString()}`} />
                    <Legend wrapperStyle={{ fontSize: 12 }} />
                    <Bar dataKey="revenue" fill="#2563EB" name={lang === "es" ? "Ingresos" : "Revenue"} />
                    <Bar dataKey="expenses" fill="#B89154" name={t("budget.expenses")} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>

            <div>
              <Overline>{t("budget.cash_flow")}</Overline>
              <div className="h-72 mt-3" style={{ minWidth: 0, minHeight: 240 }}>
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={horizon === "y1" ? projection : projection5y} margin={{ top: 16, right: 12, left: 0, bottom: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#E8E6E1" vertical={false} />
                    <XAxis dataKey="month" stroke="#8A8886" fontSize={11} interval={horizon === "y5" ? 5 : 0} />
                    <YAxis stroke="#8A8886" fontSize={12} />
                    <Tooltip contentStyle={{ background: "#fff", border: "1px solid #E8E6E1", borderRadius: 2, fontFamily: "IBM Plex Mono, monospace", fontSize: 12 }} formatter={(val) => `€${Number(val).toLocaleString()}`} />
                    <Legend wrapperStyle={{ fontSize: 12 }} />
                    <Line type="monotone" dataKey="cashFlow" stroke="#3A5A6B" strokeWidth={2} dot={{ r: horizon === "y5" ? 1 : 3 }} name={t("budget.cash_flow")} />
                    <Line type="monotone" dataKey="cumulative" stroke="#2563EB" strokeWidth={2} dot={{ r: horizon === "y5" ? 1 : 3 }} name={lang === "es" ? "Caja acumulada" : "Cumulative cash"} />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            </div>
          </div>
        )}

        {/* INVESTMENT */}
        {tab === "investment" && (
          <div data-testid="budget-tab-investment" className="space-y-5">
            <p className="text-sm text-[#5C5B5A] max-w-2xl">
              {lang === "es"
                ? "Gastos de arranque únicos (constitución, equipamiento, web, branding, fondo de maniobra inicial)."
                : "One-time launch costs (incorporation, equipment, web, branding, initial working capital)."}
            </p>
            <div>
              <FieldLabel>{t("fin.initial_capital")}</FieldLabel>
              <TextInput type="number" value={v.initial_capital} onChange={(x) => set("initial_capital", parseFloat(x) || 0)} testId="fin-initial-capital-input" />
              <p className="text-xs text-[#8A8886] mt-1">
                {lang === "es" ? "Aportación inicial de socios + financiación pre-operativa" : "Founders' contribution + pre-operating financing"}
              </p>
            </div>
            <CategoryList
              items={v.startup_costs || []}
              onAdd={startup.add}
              onUpdate={startup.update}
              onRemove={startup.remove}
              addLabel={t("budget.add_item")}
              testIdPrefix="startup"
              placeholder={t("budget.startup")}
              total={totalStartup}
              lang={lang}
            />
          </div>
        )}

        {/* COSTS */}
        {tab === "costs" && (
          <div data-testid="budget-tab-costs" className="space-y-8">
            <p className="text-sm text-[#5C5B5A] max-w-3xl">
              {lang === "es"
                ? "Cada gasto puede ser Puntual (compra de equipo, licencia anual pagada de golpe, alquiler de un estudio en un mes concreto) o Recurrente (sueldo, alquiler mensual, SaaS). Define cuándo empieza para que el Cash Flow refleje la realidad mes a mes."
                : "Each expense can be One-time (equipment purchase, annual license paid up front, studio rental in a specific month) or Recurring (salary, monthly rent, SaaS). Set when it starts so the Cash Flow reflects reality month by month."}
            </p>
            <CategoryList
              items={v.fixed_monthly || []}
              onAdd={fixed.add}
              onUpdate={fixed.update}
              onRemove={fixed.remove}
              addLabel={t("budget.add_item")}
              testIdPrefix="fixed"
              placeholder={t("budget.fixed")}
              yearTotal={fin.totals.fixedYearly}
              nameKey="item"
              accentRecurring="#3A5A6B"
              accentOneTime="#B89154"
              lang={lang}
            />
            <CategoryList
              items={v.variable_monthly || []}
              onAdd={variable.add}
              onUpdate={variable.update}
              onRemove={variable.remove}
              addLabel={t("budget.add_item")}
              testIdPrefix="variable"
              placeholder={t("budget.variable")}
              yearTotal={fin.totals.variableYearly}
              nameKey="item"
              accentRecurring="#9E6B3F"
              accentOneTime="#B89154"
              lang={lang}
            />
            <div className="border-t-2 border-[#1A1A1A] pt-4 grid grid-cols-2 sm:grid-cols-4 gap-3 font-mono" style={{ fontFamily: '"IBM Plex Mono", monospace' }}>
              <div>
                <Overline>{lang === "es" ? "Σ Gasto M1" : "Σ Month 1 expenses"}</Overline>
                <div className="text-xl text-[#1A1A1A] mt-1" data-testid="costs-total-burn">€{totalMonthlyExpenses.toLocaleString()}</div>
              </div>
              <div>
                <Overline>{lang === "es" ? "Σ Año 1" : "Σ Year 1"}</Overline>
                <div className="text-xl text-[#1A1A1A] mt-1">€{(fin.totals.expenses || 0).toLocaleString()}</div>
              </div>
              <div>
                <Overline>{lang === "es" ? "Σ Año 5" : "Σ Year 5"}</Overline>
                <div className="text-xl text-[#1A1A1A] mt-1">€{(fin.fiveYear[4]?.expenses || 0).toLocaleString()}</div>
              </div>
              <div>
                <Overline>{lang === "es" ? "Σ Acumulado 5y" : "Σ 5-yr cumulative"}</Overline>
                <div className="text-xl text-[#1A1A1A] mt-1">€{fiveYearCumulativeExpenses.toLocaleString()}</div>
              </div>
            </div>
          </div>
        )}

        {/* REVENUE */}
        {tab === "revenue" && (
          <div data-testid="budget-tab-revenue" className="space-y-8">
            <p className="text-sm text-[#5C5B5A] max-w-3xl">
              {lang === "es"
                ? "Cada ingreso puede ser un pago Único (subvención, preventa, hito de licencia) o Recurrente (cuota mensual, suscripción, retainer). Indica cuándo empieza y el sistema lo proyectará sobre los próximos 5 años (60 meses) en el Cash Flow."
                : "Each revenue can be a One-time payment (grant, pre-sale, license milestone) or Recurring (monthly fee, subscription, retainer). Set when it starts and the system will project it across the next 5 years (60 months) in the Cash Flow."}
            </p>

            {/* Streams */}
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <Overline>{t("budget.revenue")}</Overline>
                <SecondaryButton onClick={addRevenue} testId="add-revenue-btn">
                  <Plus className="h-4 w-4" strokeWidth={1.5} />
                  {t("budget.add_revenue")}
                </SecondaryButton>
              </div>
              {(v.revenue_streams || []).length === 0 ? (
                <div className="text-sm text-[#8A8886] py-6 text-center border border-dashed border-[#E8E6E1] rounded-sm">
                  {lang === "es" ? "Sin ingresos. Añade la primera fuente." : "No streams. Add the first one."}
                </div>
              ) : (
                <div className="space-y-3">
                  {v.revenue_streams.map((rRaw, i) => {
                    const r = {
                      type: rRaw.type === "one-time" ? "one-time" : "recurring",
                      amount: rRaw.amount != null ? rRaw.amount : (rRaw.monthly_amount ?? 0),
                      start_month: rRaw.start_month || 1,
                      recurrence: rRaw.recurrence || "monthly",
                      monthly_growth_rate: rRaw.monthly_growth_rate != null
                        ? rRaw.monthly_growth_rate
                        : (rRaw.growth_rate ?? 0),
                      name: rRaw.name || "",
                    };
                    return (
                      <div
                        key={i}
                        className="border border-[#E8E6E1] bg-white rounded-sm p-4 space-y-3"
                        data-testid={`revenue-row-${i}`}
                      >
                        {/* Row 1 — concept + type toggle + delete */}
                        <div className="grid grid-cols-1 md:grid-cols-[1fr_auto_auto] gap-3 items-center">
                          <TextInput
                            value={r.name}
                            onChange={(x) => updateRevenue(i, "name", x)}
                            placeholder={lang === "es" ? "Concepto (ej. Subvención ICAA, Cuota cliente A)" : "Concept (e.g. ICAA grant, Customer A retainer)"}
                            testId={`revenue-${i}-name`}
                          />
                          <div className="inline-flex border border-[#E8E6E1] rounded-sm overflow-hidden">
                            <button
                              type="button"
                              onClick={() => updateRevenue(i, "type", "one-time")}
                              data-testid={`revenue-${i}-type-onetime`}
                              className={`px-3 py-2 text-xs uppercase tracking-[0.16em] inline-flex items-center gap-1.5 transition-colors ${
                                r.type === "one-time"
                                  ? "bg-[#B89154] text-[#FBFAF7]"
                                  : "bg-white text-[#5C5B5A] hover:text-[#1A1A1A]"
                              }`}
                            >
                              <Zap className="h-3.5 w-3.5" strokeWidth={1.5} />
                              {lang === "es" ? "Puntual" : "One-time"}
                            </button>
                            <button
                              type="button"
                              onClick={() => updateRevenue(i, "type", "recurring")}
                              data-testid={`revenue-${i}-type-recurring`}
                              className={`px-3 py-2 text-xs uppercase tracking-[0.16em] inline-flex items-center gap-1.5 border-l border-[#E8E6E1] transition-colors ${
                                r.type === "recurring"
                                  ? "bg-[#2563EB] text-[#F9F8F6]"
                                  : "bg-white text-[#5C5B5A] hover:text-[#1A1A1A]"
                              }`}
                            >
                              <Repeat className="h-3.5 w-3.5" strokeWidth={1.5} />
                              {lang === "es" ? "Recurrente" : "Recurring"}
                            </button>
                          </div>
                          <button
                            onClick={() => removeRevenue(i)}
                            className="text-[#8A8886] hover:text-[#9E2A2B] justify-self-end"
                            data-testid={`revenue-${i}-remove`}
                            aria-label="remove"
                          >
                            <Trash2 className="h-4 w-4" strokeWidth={1.5} />
                          </button>
                        </div>

                        {/* Row 2 — amount + start + (if recurring) recurrence + growth */}
                        <div className={`grid gap-3 ${
                          r.type === "recurring"
                            ? "grid-cols-2 md:grid-cols-4"
                            : "grid-cols-2"
                        }`}>
                          <div>
                            <FieldLabel>
                              {r.type === "one-time"
                                ? (lang === "es" ? "Importe (€)" : "Amount (€)")
                                : (lang === "es" ? "Importe inicial (€)" : "Initial amount (€)")}
                            </FieldLabel>
                            <TextInput
                              type="number"
                              value={r.amount}
                              onChange={(x) => updateRevenue(i, "amount", parseFloat(x) || 0)}
                              testId={`revenue-${i}-amount`}
                            />
                          </div>
                          <div>
                            <FieldLabel>{lang === "es" ? "Empieza en" : "Starts at"}</FieldLabel>
                            <select
                              value={r.start_month}
                              onChange={(e) => updateRevenue(i, "start_month", parseInt(e.target.value, 10) || 1)}
                              className="w-full border border-[#E8E6E1] bg-white rounded-sm focus:ring-1 focus:ring-[#2563EB] focus:border-[#2563EB] text-base px-3 py-2.5 outline-none transition-all"
                              data-testid={`revenue-${i}-start`}
                            >
                              {Array.from({ length: HORIZON_MONTHS }, (_, idx) => idx + 1).map((m) => {
                                const yr = Math.floor((m - 1) / 12) + 1;
                                const mInYr = ((m - 1) % 12) + 1;
                                return (
                                  <option key={m} value={m}>
                                    {`M${m} · ${lang === "es" ? "Año" : "Year"} ${yr} mes ${mInYr}`}
                                  </option>
                                );
                              })}
                            </select>
                          </div>
                          {r.type === "recurring" && (
                            <>
                              <div>
                                <FieldLabel>{lang === "es" ? "Periodicidad" : "Recurrence"}</FieldLabel>
                                <select
                                  value={r.recurrence}
                                  onChange={(e) => updateRevenue(i, "recurrence", e.target.value)}
                                  className="w-full border border-[#E8E6E1] bg-white rounded-sm focus:ring-1 focus:ring-[#2563EB] focus:border-[#2563EB] text-base px-3 py-2.5 outline-none transition-all"
                                  data-testid={`revenue-${i}-recurrence`}
                                >
                                  <option value="monthly">{lang === "es" ? "Mensual" : "Monthly"}</option>
                                  <option value="quarterly">{lang === "es" ? "Trimestral" : "Quarterly"}</option>
                                  <option value="annual">{lang === "es" ? "Anual" : "Annual"}</option>
                                </select>
                              </div>
                              <div>
                                <FieldLabel hint={lang === "es" ? "% de crecimiento aplicado mes a mes (compuesto)" : "% growth applied month over month (compound)"}>
                                  {lang === "es" ? "Crecimiento / mes (%)" : "Growth / month (%)"}
                                </FieldLabel>
                                <TextInput
                                  type="number"
                                  value={r.monthly_growth_rate}
                                  onChange={(x) => updateRevenue(i, "monthly_growth_rate", parseFloat(x) || 0)}
                                  testId={`revenue-${i}-growth`}
                                />
                              </div>
                            </>
                          )}
                        </div>
                      </div>
                    );
                  })}
                  <div className="border-t border-[#E8E6E1] pt-3 mt-3 grid grid-cols-2 sm:grid-cols-4 gap-3">
                    <div>
                      <Overline>{lang === "es" ? "Σ Año 1" : "Σ Year 1"}</Overline>
                      <div className="font-mono text-lg text-[#1A1A1A] mt-1" style={{ fontFamily: '"IBM Plex Mono", monospace' }}>
                        €{(fin.fiveYear[0]?.revenue || 0).toLocaleString()}
                      </div>
                    </div>
                    <div>
                      <Overline>{lang === "es" ? "Σ Año 3" : "Σ Year 3"}</Overline>
                      <div className="font-mono text-lg text-[#1A1A1A] mt-1" style={{ fontFamily: '"IBM Plex Mono", monospace' }}>
                        €{(fin.fiveYear[2]?.revenue || 0).toLocaleString()}
                      </div>
                    </div>
                    <div>
                      <Overline>{lang === "es" ? "Σ Año 5" : "Σ Year 5"}</Overline>
                      <div className="font-mono text-lg text-[#1A1A1A] mt-1" style={{ fontFamily: '"IBM Plex Mono", monospace' }}>
                        €{(fin.fiveYear[4]?.revenue || 0).toLocaleString()}
                      </div>
                    </div>
                    <div>
                      <Overline>{lang === "es" ? "Σ Acumulado 5y" : "Σ 5-yr cumulative"}</Overline>
                      <div className="font-mono text-lg text-[#2563EB] mt-1" style={{ fontFamily: '"IBM Plex Mono", monospace' }}>
                        €{fiveYearCumulativeRevenue.toLocaleString()}
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Unit economics */}
            <div className="border-t border-[#E8E6E1] pt-6">
              <Overline>{lang === "es" ? "Unit economics" : "Unit economics"}</Overline>
              <p className="text-xs text-[#8A8886] mt-1 mb-4">
                {lang === "es" ? "Ticket medio y coste variable por cliente. Usado para break-even." : "Average ticket and variable cost per customer. Used for break-even."}
              </p>
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <FieldLabel>{t("fin.unit_revenue")}</FieldLabel>
                  <TextInput type="number" value={v.unit_avg_revenue} onChange={(x) => set("unit_avg_revenue", parseFloat(x) || 0)} testId="fin-unit-revenue-input" />
                </div>
                <div>
                  <FieldLabel>{t("fin.unit_variable")}</FieldLabel>
                  <TextInput type="number" value={v.unit_avg_variable_cost} onChange={(x) => set("unit_avg_variable_cost", parseFloat(x) || 0)} testId="fin-unit-variable-input" />
                </div>
              </div>
            </div>
          </div>
        )}

        {/* STATEMENTS: P&L + Balance + Break-even */}
        {tab === "statements" && (
          <div data-testid="budget-tab-statements" className="space-y-8">
            {/* Assumptions strip */}
            <div className="border border-[#E8E6E1] bg-[#FBFAF7] rounded-sm p-5">
              <Overline>{t("fin.assumptions")}</Overline>
              <div className="mt-4 grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
                <div>
                  <FieldLabel>{t("fin.tax_rate")} (%)</FieldLabel>
                  <TextInput type="number" value={v.tax_rate} onChange={(x) => set("tax_rate", parseFloat(x) || 0)} testId="fin-tax-rate-input" />
                </div>
                <div>
                  <FieldLabel hint={lang === "es" ? "Calculado automáticamente a partir de tus ingresos en la pestaña Ingresos (cada uno con su tipo, mes de inicio y recurrencia)." : "Auto-derived from the streams you set in the Revenue tab (each with its own type, start month and recurrence)."}>
                    {lang === "es" ? "Crecimiento ingresos / año" : "Yearly revenue growth"}
                  </FieldLabel>
                  <div className="border border-[#E8E6E1] bg-[#FBFAF7] rounded-sm px-3 py-2.5 text-sm text-[#5C5B5A]" data-testid="fin-rev-growth-info">
                    {fin.fiveYear && fin.fiveYear.length >= 2 && fin.fiveYear[0].revenue > 0
                      ? `${(((fin.fiveYear[1].revenue / fin.fiveYear[0].revenue) - 1) * 100).toFixed(1)}% Y1→Y2 (auto)`
                      : (lang === "es" ? "Define ingresos en la pestaña Ingresos" : "Define streams in the Revenue tab")}
                  </div>
                </div>
                <div>
                  <FieldLabel hint={lang === "es" ? "Calculado a partir de los gastos definidos por línea en la pestaña Costes mensuales." : "Auto-derived from the per-line expenses set in the Monthly costs tab."}>
                    {lang === "es" ? "Crecimiento gastos / año" : "Yearly expense growth"}
                  </FieldLabel>
                  <div className="border border-[#E8E6E1] bg-[#FBFAF7] rounded-sm px-3 py-2.5 text-sm text-[#5C5B5A]" data-testid="fin-exp-growth-info">
                    {fin.fiveYear && fin.fiveYear.length >= 2 && fin.fiveYear[0].expenses > 0
                      ? `${(((fin.fiveYear[1].expenses / fin.fiveYear[0].expenses) - 1) * 100).toFixed(1)}% Y1→Y2 (auto)`
                      : (lang === "es" ? "Define gastos en Costes mensuales" : "Define expenses in Monthly costs")}
                  </div>
                </div>
              </div>
              <div className="mt-5">
                <FieldLabel hint={lang === "es" ? "Justifica ingresos, costes, runway, unit economics, escenarios. Cuanto más detallado, más sólido el plan." : "Justify revenue, costs, runway, unit economics, scenarios. The more detailed, the stronger the plan."}>
                  {lang === "es" ? "Assumptions y memorias" : "Assumptions & narrative"}
                </FieldLabel>
                <TextArea
                  value={v.assumptions || ""}
                  onChange={(x) => set("assumptions", x)}
                  rows={20}
                  testId="fin-assumptions-textarea"
                />
              </div>
            </div>

            {/* P&L */}
            <div>
              <Overline>{t("fin.pl")}</Overline>
              <p className="text-xs text-[#8A8886] mt-1 mb-3">{t("fin.pl_subtitle")}</p>
              <table className="w-full text-sm" data-testid="pl-table">
                <tbody className="font-mono" style={{ fontFamily: '"IBM Plex Mono", monospace' }}>
                  <tr className="border-b-2 border-[#1A1A1A]">
                    <td className="py-3 uppercase tracking-wide text-xs text-[#5C5B5A]">{t("fin.revenue")}</td>
                    <td className="py-3 text-right text-lg text-[#1A1A1A]">€{Math.round(fin.pl.revenue).toLocaleString()}</td>
                  </tr>
                  <tr className="border-b border-[#E8E6E1]">
                    <td className="py-2 pl-4 text-[#5C5B5A]">− {t("fin.cogs")}</td>
                    <td className="py-2 text-right text-[#1A1A1A]">€{Math.round(fin.pl.cogs).toLocaleString()}</td>
                  </tr>
                  <tr className="border-b-2 border-[#2563EB]">
                    <td className="py-3 uppercase tracking-wide text-xs text-[#2563EB]">= {t("fin.gross_profit")}</td>
                    <td className="py-3 text-right text-lg text-[#2563EB]">
                      €{Math.round(fin.pl.grossProfit).toLocaleString()}
                      <span className="text-xs text-[#8A8886] ml-3">({fin.pl.grossMarginPct.toFixed(1)}%)</span>
                    </td>
                  </tr>
                  <tr className="border-b border-[#E8E6E1]">
                    <td className="py-2 pl-4 text-[#5C5B5A]">− {t("fin.opex")}</td>
                    <td className="py-2 text-right text-[#1A1A1A]">€{Math.round(fin.pl.opex).toLocaleString()}</td>
                  </tr>
                  <tr className="border-b-2 border-[#2563EB]">
                    <td className="py-3 uppercase tracking-wide text-xs text-[#2563EB]">= {t("fin.operating_income")}</td>
                    <td className="py-3 text-right text-lg text-[#2563EB]">€{Math.round(fin.pl.operatingIncome).toLocaleString()}</td>
                  </tr>
                  <tr className="border-b border-[#E8E6E1]">
                    <td className="py-2 pl-4 text-[#5C5B5A]">− {t("fin.tax")} ({v.tax_rate}%)</td>
                    <td className="py-2 text-right text-[#1A1A1A]">€{Math.round(fin.pl.tax).toLocaleString()}</td>
                  </tr>
                  <tr className={`border-b-4 ${fin.pl.netIncome >= 0 ? "border-[#2563EB]" : "border-[#9E2A2B]"}`}>
                    <td className="py-4 uppercase tracking-wide text-sm font-semibold text-[#1A1A1A]">= {t("fin.net_income")}</td>
                    <td className={`py-4 text-right text-2xl font-semibold ${fin.pl.netIncome >= 0 ? "text-[#2563EB]" : "text-[#9E2A2B]"}`} data-testid="pl-net-income">
                      €{Math.round(fin.pl.netIncome).toLocaleString()}
                      <span className="text-xs text-[#8A8886] ml-3 font-normal">({fin.pl.netMarginPct.toFixed(1)}%)</span>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>

            {/* Balance */}
            <div>
              <Overline>{t("fin.balance")}</Overline>
              <div className="mt-3 grid md:grid-cols-2 gap-6 font-mono text-sm" style={{ fontFamily: '"IBM Plex Mono", monospace' }}>
                <div className="border border-[#E8E6E1] rounded-sm p-5">
                  <Overline>{t("fin.assets")}</Overline>
                  <table className="w-full mt-3">
                    <tbody>
                      <tr className="border-b border-[#F0EFE9]"><td className="py-2 text-[#5C5B5A]">{t("fin.cash")}</td><td className="py-2 text-right">€{Math.round(fin.balance.cashEoy).toLocaleString()}</td></tr>
                      <tr className="border-b border-[#F0EFE9]"><td className="py-2 text-[#5C5B5A]">{t("fin.fixed_assets")}</td><td className="py-2 text-right">€{Math.round(fin.balance.fixedAssets).toLocaleString()}</td></tr>
                      <tr className="border-t-2 border-[#1A1A1A]"><td className="py-3 uppercase text-xs">{t("fin.total_assets")}</td><td className="py-3 text-right text-lg" data-testid="balance-total-assets">€{Math.round(fin.balance.totalAssets).toLocaleString()}</td></tr>
                    </tbody>
                  </table>
                </div>
                <div className="border border-[#E8E6E1] rounded-sm p-5">
                  <Overline>{t("fin.liabilities")} + {t("fin.equity")}</Overline>
                  <table className="w-full mt-3">
                    <tbody>
                      <tr className="border-b border-[#F0EFE9]"><td className="py-2 text-[#5C5B5A]">{t("fin.liabilities")}</td><td className="py-2 text-right">€{Math.round(fin.balance.totalLiabilities).toLocaleString()}</td></tr>
                      <tr className="border-b border-[#F0EFE9]"><td className="py-2 text-[#5C5B5A]">{t("fin.paid_in")}</td><td className="py-2 text-right">€{Math.round(fin.balance.paidInCapital).toLocaleString()}</td></tr>
                      <tr className="border-b border-[#F0EFE9]"><td className="py-2 text-[#5C5B5A]">{t("fin.retained")}</td><td className={`py-2 text-right ${fin.balance.retainedEarnings < 0 ? "text-[#9E2A2B]" : ""}`}>€{Math.round(fin.balance.retainedEarnings).toLocaleString()}</td></tr>
                      <tr className="border-t-2 border-[#1A1A1A]"><td className="py-3 uppercase text-xs">{t("fin.total_liab_eq")}</td><td className="py-3 text-right text-lg" data-testid="balance-total-liab-eq">€{Math.round(fin.balance.totalLiabilities + fin.balance.totalEquity).toLocaleString()}</td></tr>
                    </tbody>
                  </table>
                </div>
              </div>
            </div>

            {/* Break-even */}
            <div>
              <Overline>{t("fin.breakeven")}</Overline>
              <p className="text-xs text-[#8A8886] mt-1 mb-3">{t("fin.breakeven_subtitle")}</p>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 font-mono" style={{ fontFamily: '"IBM Plex Mono", monospace' }}>
                <div className="border border-[#E8E6E1] rounded-sm p-4">
                  <Overline>{t("fin.contribution_margin")}</Overline>
                  <div className="text-2xl mt-2 text-[#1A1A1A]" data-testid="be-contribution-margin">€{fin.be.contributionMargin.toFixed(0)}</div>
                  <div className="text-xs text-[#8A8886] mt-1">{fin.be.contributionMarginPct.toFixed(1)}%</div>
                </div>
                <div className="border border-[#E8E6E1] rounded-sm p-4">
                  <Overline>{t("fin.customers_per_month")}</Overline>
                  <div className="text-2xl mt-2 text-[#2563EB]" data-testid="be-customers">
                    {fin.be.customersPerMonth != null ? Math.ceil(fin.be.customersPerMonth) : "—"}
                  </div>
                </div>
                <div className="border border-[#E8E6E1] rounded-sm p-4">
                  <Overline>{t("fin.revenue_per_month")}</Overline>
                  <div className="text-2xl mt-2 text-[#2563EB]" data-testid="be-revenue">
                    {fin.be.revenuePerMonth != null ? `€${Math.round(fin.be.revenuePerMonth).toLocaleString()}` : "—"}
                  </div>
                </div>
                <div className="border border-[#E8E6E1] rounded-sm p-4 bg-[#F5EFE6]">
                  <Overline>{t("fin.month_breakeven")}</Overline>
                  <div className="text-2xl mt-2 text-[#B89154]" data-testid="be-month">
                    {fin.be.breakEvenMonth != null ? `M${fin.be.breakEvenMonth}` : (lang === "es" ? "> 12 meses" : "> 12 months")}
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* SCENARIOS + 5-YEAR */}
        {tab === "scenarios" && (
          <div data-testid="budget-tab-scenarios" className="space-y-8">
            {/* 5-year */}
            <div>
              <Overline>{t("fin.five_year")}</Overline>
              <p className="text-xs text-[#8A8886] mt-1 mb-4">{t("fin.five_year_subtitle")}</p>
              <div className="h-72 mb-6">
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={fin.fiveYear} margin={{ top: 16, right: 12, left: 0, bottom: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#E8E6E1" vertical={false} />
                    <XAxis dataKey="year" stroke="#8A8886" fontSize={12} />
                    <YAxis stroke="#8A8886" fontSize={12} />
                    <Tooltip contentStyle={{ background: "#fff", border: "1px solid #E8E6E1", borderRadius: 2, fontFamily: "IBM Plex Mono, monospace", fontSize: 12 }} formatter={(val) => `€${Number(val).toLocaleString()}`} />
                    <Legend wrapperStyle={{ fontSize: 12 }} />
                    <Line type="monotone" dataKey="revenue" stroke="#2563EB" strokeWidth={2} dot={{ r: 4 }} name={t("fin.revenue")} />
                    <Line type="monotone" dataKey="expenses" stroke="#B89154" strokeWidth={2} dot={{ r: 4 }} name={t("fin.opex")} />
                    <Line type="monotone" dataKey="net" stroke="#3A5A6B" strokeWidth={2} dot={{ r: 4 }} name={t("fin.net_income")} />
                  </LineChart>
                </ResponsiveContainer>
              </div>
              <table className="w-full text-sm font-mono" style={{ fontFamily: '"IBM Plex Mono", monospace' }}>
                <thead>
                  <tr className="border-b-2 border-[#1A1A1A] text-left">
                    <th className="py-2 text-xs uppercase tracking-wide">{t("fin.year")}</th>
                    <th className="py-2 text-xs uppercase tracking-wide text-right">{t("fin.revenue")}</th>
                    <th className="py-2 text-xs uppercase tracking-wide text-right">{t("fin.opex")}</th>
                    <th className="py-2 text-xs uppercase tracking-wide text-right">{t("fin.tax")}</th>
                    <th className="py-2 text-xs uppercase tracking-wide text-right">{t("fin.net_income")}</th>
                    <th className="py-2 text-xs uppercase tracking-wide text-right">{t("fin.cumulative_net")}</th>
                  </tr>
                </thead>
                <tbody data-testid="five-year-table">
                  {fin.fiveYear.map((row) => (
                    <tr key={row.yearIdx} className="border-b border-[#F0EFE9]">
                      <td className="py-2 text-[#1A1A1A]">{row.year}</td>
                      <td className="py-2 text-right">€{row.revenue.toLocaleString()}</td>
                      <td className="py-2 text-right">€{row.expenses.toLocaleString()}</td>
                      <td className="py-2 text-right text-[#8A8886]">€{row.tax.toLocaleString()}</td>
                      <td className={`py-2 text-right font-semibold ${row.net >= 0 ? "text-[#2563EB]" : "text-[#9E2A2B]"}`}>€{row.net.toLocaleString()}</td>
                      <td className={`py-2 text-right font-semibold ${row.cumulative >= 0 ? "text-[#2563EB]" : "text-[#9E2A2B]"}`}>€{row.cumulative.toLocaleString()}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* === Stress Test (interactive sliders) === */}
            <div className="border-t border-[#E8E6E1] pt-8" data-testid="stress-test-section">
              <div className="flex items-center justify-between flex-wrap gap-3">
                <div>
                  <Overline>{lang === "es" ? "Stress Test interactivo" : "Interactive Stress Test"}</Overline>
                  <p className="text-xs text-[#8A8886] mt-1 max-w-2xl">
                    {lang === "es"
                      ? "Mueve los deslizadores para simular escenarios reales: ¿qué pasa si la subvención llega 3 meses tarde? ¿si el fichaje cuesta un 20% más? ¿si los ingresos caen un 30%? Las curvas se recalculan al instante. Tus datos guardados no se tocan."
                      : "Drag the sliders to simulate real scenarios: what if the grant comes 3 months late? If the new hire costs 20% more? If revenue drops 30%? Curves recompute instantly. Your saved data is never touched."}
                  </p>
                </div>
                <SecondaryButton
                  onClick={() => setStress({ revenueCut: 0, revenueDelay: 0, expenseHike: 0, startupOverrun: 0 })}
                  testId="stress-reset-btn"
                  disabled={!stressActive}
                >
                  {lang === "es" ? "Restablecer" : "Reset"}
                </SecondaryButton>
              </div>

              <div className="mt-5 grid grid-cols-1 md:grid-cols-2 gap-5 border border-[#E8E6E1] bg-[#FBFAF7] rounded-sm p-5">
                {[
                  { key: "revenueCut",     label: lang === "es" ? "Caída de ingresos" : "Revenue drop", suffix: "%", min: 0, max: 50, step: 5, color: "#9E2A2B" },
                  { key: "revenueDelay",   label: lang === "es" ? "Retraso de ingresos" : "Revenue delay", suffix: lang === "es" ? " meses" : " mo", min: 0, max: 12, step: 1, color: "#B89154" },
                  { key: "expenseHike",    label: lang === "es" ? "Sobrecoste en gastos" : "Expense overrun", suffix: "%", min: 0, max: 50, step: 5, color: "#9E6B3F" },
                  { key: "startupOverrun", label: lang === "es" ? "Sobrecoste startup" : "Startup overrun", suffix: "%", min: 0, max: 100, step: 10, color: "#3A5A6B" },
                ].map((s) => (
                  <div key={s.key}>
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-xs uppercase tracking-[0.18em] text-[#5C5B5A]">{s.label}</span>
                      <span className="font-mono text-sm" style={{ color: s.color, fontFamily: '"IBM Plex Mono", monospace' }} data-testid={`stress-${s.key}-value`}>
                        {stress[s.key] === 0 ? "—" : `+${stress[s.key]}${s.suffix}`}
                      </span>
                    </div>
                    <input
                      type="range"
                      min={s.min}
                      max={s.max}
                      step={s.step}
                      value={stress[s.key]}
                      onChange={(e) => setStress((p) => ({ ...p, [s.key]: parseInt(e.target.value, 10) || 0 }))}
                      className="w-full"
                      style={{ accentColor: s.color }}
                      data-testid={`stress-${s.key}-slider`}
                    />
                    <div className="flex justify-between text-[10px] text-[#8A8886] mt-1" style={{ fontFamily: '"IBM Plex Mono", monospace' }}>
                      <span>0</span>
                      <span>{s.max}{s.suffix}</span>
                    </div>
                  </div>
                ))}
              </div>

              {stressActive && stressKpis && (
                <>
                  <div className="mt-5 grid grid-cols-2 md:grid-cols-4 gap-3 font-mono" style={{ fontFamily: '"IBM Plex Mono", monospace' }} data-testid="stress-kpis">
                    <div className="border border-[#E8E6E1] rounded-sm p-4">
                      <div className="text-[10px] uppercase tracking-[0.18em] text-[#8A8886]">{lang === "es" ? "Break-even" : "Break-even"}</div>
                      <div className="mt-1.5 text-lg text-[#1A1A1A]">
                        {stressKpis.stressedBE ? `M${stressKpis.stressedBE}` : "—"}
                      </div>
                      <div className="text-[10px] text-[#8A8886] mt-0.5">
                        {lang === "es" ? "Base: " : "Base: "}{stressKpis.baselineBE ? `M${stressKpis.baselineBE}` : "—"}
                      </div>
                    </div>
                    <div className="border border-[#E8E6E1] rounded-sm p-4">
                      <div className="text-[10px] uppercase tracking-[0.18em] text-[#8A8886]">{lang === "es" ? "Cash flow Y1" : "Y1 cash flow"}</div>
                      <div className={`mt-1.5 text-lg ${stressKpis.stressedY1 < 0 ? "text-[#9E2A2B]" : "text-[#1A1A1A]"}`}>
                        €{Math.round(stressKpis.stressedY1).toLocaleString()}
                      </div>
                      <div className="text-[10px] text-[#8A8886] mt-0.5">
                        Δ €{Math.round(stressKpis.stressedY1 - stressKpis.baselineY1).toLocaleString()}
                      </div>
                    </div>
                    <div className="border border-[#E8E6E1] rounded-sm p-4">
                      <div className="text-[10px] uppercase tracking-[0.18em] text-[#8A8886]">{lang === "es" ? "Caja mínima 5y" : "Lowest cash 5y"}</div>
                      <div className={`mt-1.5 text-lg ${stressKpis.stressedLowest < 0 ? "text-[#9E2A2B]" : "text-[#1A1A1A]"}`}>
                        €{Math.round(stressKpis.stressedLowest).toLocaleString()}
                      </div>
                      <div className="text-[10px] text-[#8A8886] mt-0.5">
                        {lang === "es" ? "Base: €" : "Base: €"}{Math.round(stressKpis.baselineLowest).toLocaleString()}
                      </div>
                    </div>
                    <div className={`border rounded-sm p-4 ${stressKpis.survives ? "bg-[#2563EB] text-[#F9F8F6] border-[#2563EB]" : "bg-[#9E2A2B] text-[#F9F8F6] border-[#9E2A2B]"}`} data-testid="stress-verdict">
                      <div className="text-[10px] uppercase tracking-[0.18em] opacity-80">{lang === "es" ? "Veredicto" : "Verdict"}</div>
                      <div className="mt-1.5 text-lg">
                        {stressKpis.survives
                          ? (lang === "es" ? "Sobrevive" : "Survives")
                          : (lang === "es" ? `Faltan €${Math.round(stressKpis.extraCapitalNeeded).toLocaleString()}` : `€${Math.round(stressKpis.extraCapitalNeeded).toLocaleString()} short`)}
                      </div>
                      <div className="text-[10px] opacity-80 mt-0.5" data-testid="capital-required-info">
                        {stressKpis.survives
                          ? (lang === "es" ? "con capital inicial" : "with initial capital")
                          : (lang === "es"
                              ? `Necesitas ronda extra de €${Math.round(stressKpis.extraCapitalNeeded).toLocaleString()}`
                              : `Need extra round of €${Math.round(stressKpis.extraCapitalNeeded).toLocaleString()}`)}
                      </div>
                    </div>
                  </div>

                  <div className="h-72 mt-6">
                    <ResponsiveContainer width="100%" height="100%">
                      <LineChart data={stressCompareData} margin={{ top: 16, right: 12, left: 0, bottom: 0 }}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#E8E6E1" vertical={false} />
                        <XAxis dataKey="month" stroke="#8A8886" fontSize={11} interval={5} />
                        <YAxis stroke="#8A8886" fontSize={12} />
                        <Tooltip contentStyle={{ background: "#fff", border: "1px solid #E8E6E1", borderRadius: 2, fontFamily: "IBM Plex Mono, monospace", fontSize: 12 }} formatter={(val) => `€${Number(val).toLocaleString()}`} />
                        <Legend wrapperStyle={{ fontSize: 12 }} />
                        <Line type="monotone" dataKey="baseline" stroke="#2563EB" strokeWidth={2} dot={{ r: 1 }} name={lang === "es" ? "Base (acumulado)" : "Baseline (cumulative)"} />
                        <Line type="monotone" dataKey="stressed" stroke="#9E2A2B" strokeWidth={2} strokeDasharray="5 3" dot={{ r: 1 }} name={lang === "es" ? "Estresado (acumulado)" : "Stressed (cumulative)"} />
                      </LineChart>
                    </ResponsiveContainer>
                  </div>
                </>
              )}
            </div>

            {/* Scenarios */}
            <div className="border-t border-[#E8E6E1] pt-8">
              <Overline>{t("fin.scenarios")}</Overline>
              <p className="text-xs text-[#8A8886] mt-1 mb-4">{t("fin.scenarios_subtitle")}</p>

              {/* Scenario multipliers */}
              <div className="border border-[#E8E6E1] bg-[#FBFAF7] rounded-sm p-5 mb-6">
                <Overline>{lang === "es" ? "Multiplicadores" : "Multipliers"}</Overline>
                <div className="mt-3 grid grid-cols-2 md:grid-cols-4 gap-3">
                  <div>
                    <FieldLabel>{t("fin.best_rev_mult")}</FieldLabel>
                    <TextInput type="number" step="0.01" value={v.scenarios.best_revenue_mult} onChange={(x) => setScenario("best_revenue_mult", parseFloat(x) || 0)} testId="fin-best-rev-mult-input" />
                  </div>
                  <div>
                    <FieldLabel>{t("fin.worst_rev_mult")}</FieldLabel>
                    <TextInput type="number" step="0.01" value={v.scenarios.worst_revenue_mult} onChange={(x) => setScenario("worst_revenue_mult", parseFloat(x) || 0)} testId="fin-worst-rev-mult-input" />
                  </div>
                  <div>
                    <FieldLabel>{t("fin.best_exp_mult")}</FieldLabel>
                    <TextInput type="number" step="0.01" value={v.scenarios.best_expense_mult} onChange={(x) => setScenario("best_expense_mult", parseFloat(x) || 0)} testId="fin-best-exp-mult-input" />
                  </div>
                  <div>
                    <FieldLabel>{t("fin.worst_exp_mult")}</FieldLabel>
                    <TextInput type="number" step="0.01" value={v.scenarios.worst_expense_mult} onChange={(x) => setScenario("worst_expense_mult", parseFloat(x) || 0)} testId="fin-worst-exp-mult-input" />
                  </div>
                </div>
              </div>

              <div className="h-72 mb-6">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={fin.scenarios} margin={{ top: 16, right: 12, left: 0, bottom: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#E8E6E1" vertical={false} />
                    <XAxis dataKey="label" stroke="#8A8886" fontSize={12} />
                    <YAxis stroke="#8A8886" fontSize={12} />
                    <Tooltip contentStyle={{ background: "#fff", border: "1px solid #E8E6E1", borderRadius: 2, fontFamily: "IBM Plex Mono, monospace", fontSize: 12 }} formatter={(val) => `€${Number(val).toLocaleString()}`} />
                    <Legend wrapperStyle={{ fontSize: 12 }} />
                    <Bar dataKey="revenue" fill="#2563EB" name={t("fin.revenue")} />
                    <Bar dataKey="opex" fill="#B89154" name={t("fin.opex")} />
                    <Bar dataKey="netIncome" fill="#3A5A6B" name={t("fin.net_income")} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
              <div className="grid md:grid-cols-3 gap-4">
                {fin.scenarios.map((s) => {
                  const isNeg = s.netIncome < 0;
                  const accent = s.key === "best" ? "text-[#2563EB]" : s.key === "worst" ? "text-[#9E2A2B]" : "text-[#1A1A1A]";
                  return (
                    <div key={s.key} className={`border rounded-sm p-5 ${s.key === "base" ? "bg-[#F5F4F0] border-[#E8E6E1]" : "border-[#E8E6E1]"}`} data-testid={`scenario-${s.key}`}>
                      <Overline>{t(`fin.${s.key}`)}</Overline>
                      <div className="mt-3 space-y-2 text-sm font-mono" style={{ fontFamily: '"IBM Plex Mono", monospace' }}>
                        <div className="flex justify-between"><span className="text-[#5C5B5A]">{t("fin.revenue")}</span><span className="text-[#1A1A1A]">€{Math.round(s.revenue).toLocaleString()}</span></div>
                        <div className="flex justify-between"><span className="text-[#5C5B5A]">{t("fin.opex")}</span><span className="text-[#1A1A1A]">€{Math.round(s.opex + s.cogs).toLocaleString()}</span></div>
                        <div className="flex justify-between border-t border-[#E8E6E1] pt-2">
                          <span className="uppercase text-xs tracking-wide text-[#5C5B5A]">{t("fin.net_income")}</span>
                          <span className={`text-lg font-semibold ${isNeg ? "text-[#9E2A2B]" : accent}`}>€{Math.round(s.netIncome).toLocaleString()}</span>
                        </div>
                        <div className="flex justify-between text-xs text-[#8A8886]">
                          <span>{t("fin.net_margin")}</span>
                          <span>{s.netMarginPct.toFixed(1)}%</span>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        )}
      </SectionCard>
    </>
  );
}
